"use client";

import { useState } from "react";
import { useRouter } from "@/i18n/routing";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useUserActions } from "@/contexts/UserContext";
import { googleOAuthLogin } from "@/services/auth";
import { NEXT_PUBLIC_GOOGLE_CLIENT_ID } from "@/config/env";
import { stripLocaleFromPath } from "@/lib/i18n-utils";

interface GoogleTokenResponse {
  access_token: string;
}

export const useOAuth = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useTranslations("loginForm");
  const { setUser } = useUserActions();

  // Google OAuth Login
  const handleGoogleLogin = async (callbackUrl?: string) => {
    setIsLoading("google");
    setError(null);

    // Check if Google Client ID is configured
    if (!NEXT_PUBLIC_GOOGLE_CLIENT_ID) {
      setError("Google OAuth is not configured. Please contact support.");
      setIsLoading(null);
      return;
    }

    // Use provided callbackUrl or default to dashboard
    const rawRedirectUrl = callbackUrl || "/dashboard";
    const redirectUrl = stripLocaleFromPath(rawRedirectUrl);

    try {
      // Load Google API if not already loaded
      if (!window.google) {
        await loadGoogleAPI();
      }

      // Initialize Google OAuth Token Client
      const { google } = window;
      const client = google.accounts.oauth2.initTokenClient({
        client_id: NEXT_PUBLIC_GOOGLE_CLIENT_ID || "",
        scope: "email profile openid",
        callback: async (response: GoogleTokenResponse) => {
          try {
            const { data } = await googleOAuthLogin(response.access_token);

            if (data?.adminGoogleOauthLogin?.success && data.adminGoogleOauthLogin.user) {
              // Store tokens and redirect using NextAuth
              const result = await signIn("credentials", {
                email: data.adminGoogleOauthLogin.user.email,
                password: "oauth_login", // Special marker for OAuth login
                accessToken: data.adminGoogleOauthLogin.token,
                refreshToken: data.adminGoogleOauthLogin.refreshToken,
                userId: data.adminGoogleOauthLogin.user.id,
                firstName: data.adminGoogleOauthLogin.user.firstName || "",
                lastName: data.adminGoogleOauthLogin.user.lastName || "",
                redirect: false,
              });

              if (result?.ok) {
                // Set user data from OAuth response
                setUser(data.adminGoogleOauthLogin.user);
                router.push(redirectUrl);
              } else {
                setError(t("errors.sessionCreationFailed"));
              }
            } else {
              const errorMessage =
                data?.adminGoogleOauthLogin?.message || t("errors.loginFailed");
              setError(errorMessage);
            }
          } catch (err) {
            console.error("Google OAuth error:", err);

            // Handle specific GraphQL errors
            if (err instanceof Error) {
              const errorMessage = err.message;
              if (errorMessage.includes("No account found")) {
                setError(t("errors.userNotFound") || "No account found with this email address. Please register first.");
              } else if (errorMessage.includes("not active")) {
                setError(t("errors.userNotActive") || "User account is not active.");
              } else if (errorMessage.includes("not a staff member") || errorMessage.includes("NO_PERMISSIONS")) {
                setError(t("errors.noPermissions") || "Access denied - administrator privileges required.");
              } else {
                setError(errorMessage || t("errors.loginFailed"));
              }
            } else {
              setError(t("errors.loginFailed"));
            }
          } finally {
            setIsLoading(null);
          }
        },
      });

      client.requestAccessToken();
    } catch (error) {
      console.error("Google OAuth initialization error:", error);
      setError("Failed to initialize Google login");
      setIsLoading(null);
    }
  };

  return {
    handleGoogleLogin,
    isLoading,
    error,
    setError,
  };
};

// Helper function to load Google SDK script
const loadGoogleAPI = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.google) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google API"));
    document.head.appendChild(script);
  });

// Extend window object for TypeScript
declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GoogleTokenResponse) => void;
          }) => { requestAccessToken: () => void };
        };
      };
    };
  }
}

